import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { CommonConstant } from './constants';
import { SPECTRUM_AUTH_API } from './tokens';

export const requestAuthInterceptor: HttpInterceptorFn = (req, next) => {
    // Pre-signed S3 URLs carry their own signature — adding Spectrum auth headers
    // makes S3 reject the request as a malformed/duplicated authorization.
    if (req.url.indexOf('amazonaws.com') > -1) {
        return next(req);
    }

    const token = sessionStorage.getItem(CommonConstant.token);
    const orgCode = sessionStorage.getItem(CommonConstant.orgCode);
    const appName = sessionStorage.getItem(CommonConstant.appName);
    const isSmartParent = appName === 'SmartParent';
    // Group Admin sessions authenticate against a (GroupAdmin, groupCd) access
    // row — forcing 'Spectrum' here makes auth reject every group API call.
    const isGroupAdmin = appName === 'GroupAdmin';

    let headers = req.headers
        .set('Authorization', token ?? '')
        .set('SCHOOL_CODE', orgCode ?? '')
        .set('APP_NAME', isSmartParent ? 'SmartParent' : isGroupAdmin ? 'GroupAdmin' : 'Spectrum')
        .set('Accept', '*/*');

    if (isSmartParent) {
        const studentProfileId = sessionStorage.getItem(CommonConstant.studentProfileId);
        headers = headers.set('STUDENT_PROFILE_ID', studentProfileId ?? '');
    }

    if (orgCode === 'SYSTEM_ADMIN') {
        const requiredSchoolCode = sessionStorage.getItem('requiredSchoolCode');
        if (requiredSchoolCode && requiredSchoolCode !== 'SYSTEM_ADMIN') {
            headers = headers.set('REQUESTED_SCHOOL_CD', requiredSchoolCode);
        }
    }

    return next(req.clone({ headers }));
};

/**
 * Sign-in attempts answer 401 when the credentials are simply wrong. That is not
 * an expired session, so these must not trigger the redirect below — otherwise a
 * mistyped password bounces the user off the sign-in screen two seconds later.
 */
const AUTHENTICATION_PATHS = ['/auth/login', '/auth/otp/', '/auth/validateOtp'];

function isAuthenticationAttempt(url: string): boolean {
    return AUTHENTICATION_PATHS.some(path => url.includes(path));
}

export const responseInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const authBase = inject(SPECTRUM_AUTH_API);

    return next(req).pipe(
        catchError((err: any) => {
            if (err instanceof HttpErrorResponse && !isAuthenticationAttempt(req.url)) {
                if (err.status === 401 || err.status === 408 || err.status === 432) {
                    if (req.headers.has('PARENT_SESSION_TOKEN')) {
                        // Parent-portal session expired — clear only the parent session
                        // and return to the OTP login, not the staff validation screen.
                        showSessionToast();
                        localStorage.removeItem(CommonConstant.parentSession);
                        setTimeout(() => router.navigate(['/parent-portal/login']), 2000);
                    } else {
                        // Staff session expired — /validate without a token only renders a
                        // static "Session Expired" screen, so send the user to the central
                        // portal login to re-authenticate.
                        showSessionToast();
                        sessionStorage.clear();
                        setTimeout(() => { window.location.href = authBase; }, 2000);
                    }
                }
            }
            return throwError(() => err);
        })
    );
};

function showSessionToast(): void {
    const existing = document.getElementById('__session-toast__');
    if (existing) return;
    const toast = document.createElement('div');
    toast.id = '__session-toast__';
    toast.textContent = 'Your session has expired. Redirecting to login…';
    Object.assign(toast.style, {
        position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
        background: '#1e293b', color: '#fff', padding: '12px 24px',
        borderRadius: '8px', fontSize: '14px', fontFamily: 'Inter, sans-serif',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)', zIndex: '99999',
        animation: 'fadeIn 0.3s ease'
    });
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
}
